import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Receipt } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, StatCard, EmptyState, Field, usePagination, Pagination, useDesplazarAlAbrir } from '../ui';
interface Cliente {
  id: string;
  nombre: string;
  codigo?: string;
}
interface Venta {
  id: string;
  numeroFactura: string;
  clienteId: string;
  clienteNombre: string;
  fecha: string;
  cantidad: number;
  precio: number;
  total: number;
  ruta: string;
  supervisor: string;
  observaciones: string;
}
interface VentasProps {
  ventas: Venta[];
  clientes: Cliente[];
  onAddVenta: (venta: Venta) => void;
  onDeleteVenta: (id: string) => void;
  onUpdateVenta: (venta: Venta) => void;
}
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
function fmtRD(n: number) {
  return new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);
}
export function Ventas({
  ventas,
  clientes,
  onAddVenta,
  onDeleteVenta,
  onUpdateVenta
}: VentasProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Al editar un registro del final del listado, el formulario se abre arriba:
  // sin llevar la vista hasta él parece que el botón no hizo nada.
  const refForm = useDesplazarAlAbrir(showForm, editingId);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFecha, setFilterFecha] = useState('');
  const [formData, setFormData] = useState<Partial<Venta>>({
    cantidad: 0,
    precio: 0,
    total: 0
  });
  const [selectedCliente, setSelectedCliente] = useState<string>('');
  const handleCalculateTotal = (cantidad: number, precio: number) => {
    const total = cantidad * precio;
    setFormData(prev => ({
      ...prev,
      cantidad,
      precio,
      total
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.numeroFactura || !selectedCliente || !formData.fecha || !formData.cantidad || !formData.precio) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const cliente = clientes.find(c => c.id === selectedCliente);
    if (editingId) {
      onUpdateVenta({
        ...formData,
        id: editingId,
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || ''
      } as Venta);
      setEditingId(null);
    } else {
      onAddVenta({
        ...formData,
        id: Date.now().toString(),
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || ''
      } as Venta);
    }
    setFormData({
      cantidad: 0,
      precio: 0,
      total: 0
    });
    setSelectedCliente('');
    setShowForm(false);
  };
  const handleEdit = (venta: Venta) => {
    setFormData(venta);
    setSelectedCliente(venta.clienteId);
    setEditingId(venta.id);
    setShowForm(true);
  };
  const filteredVentas = ventas.filter(v => {
    const matchSearch = v.numeroFactura.toLowerCase().includes(searchTerm.toLowerCase()) || v.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFecha = !filterFecha || v.fecha === filterFecha;
    return matchSearch && matchFecha;
  });
  // Se ordena ANTES de paginar: al revés, cada página vendría ordenada por
  // su cuenta y el listado saldría descolocado entre páginas.
  const ventasOrdenadas = [...filteredVentas].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(ventasOrdenadas);
  const ventasHoy = ventas.filter(v => v.fecha === new Date().toISOString().split('T')[0]).length;
  const totalVentas = ventas.reduce((sum, v) => sum + v.total, 0);
  const totalCantidad = ventas.reduce((sum, v) => sum + v.cantidad, 0);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-1391" data-mp="2arhhli067hd9">
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-1392" data-mp="25pica2kveado">
        
        <StatCard label="Total ventas" value={String(ventas.length)} note="Facturas registradas" accent={T.info} data-id="element-1393" data-mp="1wq50h399x9ps" />
        
        <StatCard label="Hoy" value={String(ventasHoy)} note="Facturadas en el día" accent={T.ok} data-id="element-1394" data-mp="1rzetxmp430ti" />
        
        <StatCard label="Monto total" value={`RD$ ${fmtRD(totalVentas)}`} note="Acumulado histórico" accent={T.warn} data-id="element-1395" data-mp="1n1n36w07dkqw" />
        
        <StatCard label="Cantidad total" value={`${totalCantidad.toFixed(0)} gal`} note="Volumen distribuido" accent="#8B5CF6" data-id="element-1396" data-mp="1i3btqmdtlc97" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-1397" data-mp="191vn8pq4lua9">
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-1398" data-mp="3kofsmy3vuoud">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-1399" data-mp="3plphabjg6wh5">
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-1400" data-mp="3tq5ja1pxxjr3" />
            
            <input type="text" placeholder="Buscar por factura o cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-1401" data-mp="31vle25a68yqo" />
            
          </div>
          <input type="date" value={filterFecha} onChange={e => setFilterFecha(e.target.value)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-1402" data-mp="3fpcp5i3jwqoi" />
          
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            cantidad: 0,
            precio: 0,
            total: 0
          });
          setSelectedCliente('');
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-1403" data-mp="2n8s5div5qr7g">
            
            <Plus size={15} data-id="element-1404" data-mp="3vaea32w7o04z" /> Registrar Venta
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-1405" data-mp="37kblwxvf3ebn">
        {showForm && <motion.div ref={refForm} initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-1406" data-mp="37ldam55cvzbu">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-1407" data-mp="3bl3x3wnuntak">
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-1408" data-mp="vtznxyl63jdp">
              
                {editingId ? 'Editar Venta' : 'Registrar Nueva Venta'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-1409" data-mp="13ek0bpfaolst">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14
            }} data-id="element-1410" data-mp="13gd4my2fqtxn">
                
                  <Field label="Número de factura *" data-id="element-1411" data-mp="udlud1laq47l">
                    <input type="text" value={formData.numeroFactura || ''} onChange={e => setFormData({
                  ...formData,
                  numeroFactura: e.target.value
                })} style={inputStyle} placeholder="FAC-2024-0001" data-id="element-1412" data-mp="ug7zkukps7mn" />
                  
                  </Field>
                  <Field label="Fecha *" data-id="element-1413" data-mp="18bsihq0g0pu2">
                    <input type="date" value={formData.fecha || ''} onChange={e => setFormData({
                  ...formData,
                  fecha: e.target.value
                })} style={inputStyle} data-id="element-1414" data-mp="1dcg4fx9teghf" />
                  
                  </Field>
                  <Field label="Cliente *" data-id="element-1415" data-mp="25vdeqy217yn5">
                    <select value={selectedCliente} onChange={e => setSelectedCliente(e.target.value)} style={inputStyle} data-id="element-1416" data-mp="5rkp9m19lp3h">
                    
                      <option value="" data-id="element-1417" data-mp="yzv2jeg59zf">Selecciona un cliente</option>
                      {clientes.map(c => <option key={c.id} value={c.id} data-id="element-1418" data-mp="3s75cdgjs9eoi">
                          {c.nombre}{c.codigo ? ` (${c.codigo})` : ''}
                        </option>)}
                    </select>
                  </Field>
                  <Field label="Ruta" data-id="element-1419" data-mp="op3wql82qzns">
                    <input type="text" value={formData.ruta || ''} onChange={e => setFormData({
                  ...formData,
                  ruta: e.target.value
                })} style={inputStyle} placeholder="Ruta Centro" data-id="element-1420" data-mp="jr269f8bjy2a" />
                  
                  </Field>
                  <Field label="Cantidad (galones) *" data-id="element-1421" data-mp="asgnpk2ukj1i">
                    <input type="number" step="0.01" value={formData.cantidad || 0} onChange={e => handleCalculateTotal(parseFloat(e.target.value), formData.precio || 0)} style={inputStyle} placeholder="0" data-id="element-1422" data-mp="30g7mek8sw86q" />
                  
                  </Field>
                  <Field label="Precio por galón *" data-id="element-1423" data-mp="1fxez78ueizmk">
                    <input type="number" step="0.01" value={formData.precio || 0} onChange={e => handleCalculateTotal(formData.cantidad || 0, parseFloat(e.target.value))} style={inputStyle} placeholder="0.00" data-id="element-1424" data-mp="1fxx38rnb2i51" />
                  
                  </Field>
                  <Field label="Total (RD$)" data-id="element-1425" data-mp="1tp2580e5bmij">
                    <input type="number" value={formData.total || 0} readOnly style={{
                  ...inputStyle,
                  opacity: 0.6,
                  fontWeight: 700,
                  color: T.ok
                }} data-id="element-1426" data-mp="1toa98ogmzmrq" />
                  
                  </Field>
                  <Field label="Supervisor" data-id="element-1427" data-mp="27njrdtoekqyc">
                    <input type="text" value={formData.supervisor || ''} onChange={e => setFormData({
                  ...formData,
                  supervisor: e.target.value
                })} style={inputStyle} placeholder="Nombre del supervisor" data-id="element-1428" data-mp="27m8m1pfb7b54" />
                  
                  </Field>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-1429" data-mp="riuzxaslqwbh">
                    <Field label="Observaciones" data-id="element-1430" data-mp="vk6fenhri15y">
                      <textarea value={formData.observaciones || ''} onChange={e => setFormData({
                    ...formData,
                    observaciones: e.target.value
                  })} style={{
                    ...inputStyle,
                    resize: 'vertical'
                  }} rows={3} placeholder="Notas adicionales..." data-id="element-1431" data-mp="10gx4g0bjo73i" />
                    
                    </Field>
                  </div>
                </div>
                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-1432" data-mp="1j9rc096u8wxp">
                  <button type="submit" style={primaryButton} data-id="element-1433" data-mp="1t643r1itp0pt">
                    {editingId ? 'Actualizar' : 'Registrar'} venta
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }} style={ghostButton} data-id="element-1434" data-mp="1y5gsjrt98hv1">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Tabla de Ventas */}
      <div style={{
      ...glass,
      padding: 20
    }} data-id="element-1435" data-mp="33qs2xt85w90o">
        <h3 style={{
        fontSize: 15,
        fontWeight: 800,
        marginBottom: 16,
        color: T.text
      }} data-id="element-1436" data-mp="5839dgfi2x1h">
          
          Registro de Ventas ({filteredVentas.length})
        </h3>
        {filteredVentas.length === 0 ? <EmptyState icon={<Receipt size={20} data-id="element-1438" data-mp="3rm367xiqlpug" />} title="No hay ventas registradas" hint="Registra una venta nueva o ajusta los filtros de búsqueda." data-id="element-1437" data-mp="3rmmf6oqet3wc" /> : <>
            <div style={{
          overflowX: 'auto'
        }} data-id="element-1439" data-mp="nykqsouvu67j">
              <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13
          }} data-id="element-1440" data-mp="nzvuyps8pfz6">
              
                <thead data-id="element-1441" data-mp="2df6kyviesct1">
                  <tr data-id="element-1442" data-mp="2dh1547luw0w2">
                    {[{
                  h: 'Factura',
                  a: 'left'
                }, {
                  h: 'Fecha',
                  a: 'left'
                }, {
                  h: 'Cliente',
                  a: 'left'
                }, {
                  h: 'Cantidad (gal)',
                  a: 'right'
                }, {
                  h: 'Precio',
                  a: 'right'
                }, {
                  h: 'Total (RD$)',
                  a: 'right'
                }, {
                  h: 'Ruta',
                  a: 'left'
                }, {
                  h: 'Supervisor',
                  a: 'left'
                }, {
                  h: 'Acciones',
                  a: 'center'
                }].map(({
                  h,
                  a
                }) => <th key={h} style={{
                  textAlign: a as any,
                  padding: '0 10px 10px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: T.textMuted,
                  borderBottom: `1px solid ${T.border}`
                }} data-id="element-1443" data-mp="3f38ynw2vxmtq">
                    
                        {h}
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-1444" data-mp="1r7axf3w0hsec">
                  {pageItems.map(venta => <tr key={venta.id} data-id="element-1445" data-mp="1r5zut7eayqot">
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  fontFamily: 'monospace',
                  color: T.info,
                  fontWeight: 700
                }} data-id="element-1446" data-mp="1m880y7u3kuhz">
                    
                        {venta.numeroFactura}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-1447" data-mp="6s8z1eb2puq6">
                    
                        {venta.fecha}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  fontWeight: 600,
                  color: T.text
                }} data-id="element-1448" data-mp="2tz3tlpp26l30">
                    
                        {venta.clienteNombre}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  color: T.textSecondary
                }} data-id="element-1449" data-mp="26blzhnibnawv">
                    
                        {venta.cantidad.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  color: T.textSecondary
                }} data-id="element-1450" data-mp="2k3tvve5p0zv1">
                    
                        {venta.precio.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  fontWeight: 800,
                  color: T.ok
                }} data-id="element-1451" data-mp="1f32cqmxjl3cv">
                    
                        {venta.total.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-1452" data-mp="114m4grk174sm">
                    
                        {venta.ruta || '-'}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-1453" data-mp="ncxtirliqmd8">
                    
                        {venta.supervisor || '-'}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'center'
                }} data-id="element-1454" data-mp="3ota510ts8q1">
                    
                        <div style={{
                    display: 'inline-flex',
                    gap: 6,
                    justifyContent: 'center'
                  }} data-id="element-1455" data-mp="3pycmaqc55y79">
                      
                          <button onClick={() => handleEdit(venta)} title="Editar" style={{
                      ...iconBtn,
                      color: T.info
                    }} data-id="element-1456" data-mp="mguictzhpbc4">
                        
                            <Edit2 size={14} data-id="element-1457" data-mp="cq9ash1u8wh2" />
                          </button>
                          <button onClick={() => onDeleteVenta(venta.id)} title="Eliminar" style={{
                      ...iconBtn,
                      color: T.danger
                    }} data-id="element-1458" data-mp="hob3zuszac69">
                        
                            <Trash2 size={14} data-id="element-1459" data-mp="1a783s1nkkbxq" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {filteredVentas.length ? <div style={{
          padding: '14px 16px 0'
        }} data-id="element-1460" data-mp="34mn6rnfftld2">
                <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="ventas" data-id="element-1461" data-mp="2zn27td09pd0c" />

              </div> : null}

            {/* Resumen */}
            <div style={{
          borderTop: `1px solid ${T.border}`,
          marginTop: 18,
          paddingTop: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 14
        }} data-id="element-1462" data-mp="26ymwb0gchctr">
            
              <div data-id="element-1463" data-mp="2gynhyt1os514">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1464" data-mp="7x4xrrf3zvn">
                
                  Registros
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: T.info
            }} data-id="element-1465" data-mp="1unm6vqmb6qst">
                  {filteredVentas.length}
                </div>
              </div>
              <div data-id="element-1466" data-mp="24f0b2bp7gesa">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1467" data-mp="24fi4l0khgewi">
                
                  Total galones
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: '#8B5CF6'
            }} data-id="element-1468" data-mp="2ibn6ga1ieww3">
                  {filteredVentas.reduce((sum, v) => sum + v.cantidad, 0).toFixed(2)}
                </div>
              </div>
              <div data-id="element-1469" data-mp="szl3kdzde4ix">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1470" data-mp="sz1xpvmzv6l9">
                
                  Monto total
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: T.ok
            }} data-id="element-1471" data-mp="2xedhvf4mlgm1">
                  RD$ {filteredVentas.reduce((sum, v) => sum + v.total, 0).toFixed(2)}
                </div>
              </div>
              <div data-id="element-1472" data-mp="3l7na70mj6g88">
                <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: T.textMuted,
              marginBottom: 4
            }} data-id="element-1473" data-mp="3l73qbedblhrk">
                
                  Precio promedio
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: T.warn
            }} data-id="element-1474" data-mp="1zszd0m8v5dib">
                  {(filteredVentas.reduce((sum, v) => sum + v.precio, 0) / filteredVentas.length).toFixed(2)}
                </div>
              </div>
            </div>
          </>}
      </div>
    </div>;
}